import { useState, useRef, useEffect } from "react";
// import "./login.css";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
// Removed unnecessary imports for design:
// import "mdb-ui-kit/css/mdb.min.css";
// import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Notes from "./header";
import FooterManual from "./footerManual";
import { Snackbar, Alert, AlertTitle } from '@mui/material';

const Login = () => {
  const [formData, setFormData] = useState({
    contactInfo: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [progress, setProgress] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [resendOtpLoading, setResendOtpLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Enhanced notification system states
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info', // 'success', 'error', 'warning', 'info'
    title: '',
    duration: 6000
  });

  const emailInputRef = useRef(null);

  // Corporate notification function
  const showNotification = (message, type = 'info', title = '', duration = 6000) => {
    setNotification({
      open: true,
      message,
      type,
      title,
      duration
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (showPopup) {
      // Reset all forgot password related fields when popup opens
      setEmail("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setIsOtpSent(false);
      setIsOtpVerified(false);
      
      setTimeout(() => {
        if (emailInputRef.current) {
          emailInputRef.current.focus();
        }
      }, 100);
    }
  }, [showPopup]);

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowPopup(true);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setSendOtpLoading(true);
    try {
      await axios.post(`https://car.amar.co.jp/api/authorization/auth/forgot-password/send-otp?passwordResetRequestEmailId=${email}`);
      setIsOtpSent(true);
      showNotification(
        "A verification code has been sent to your registered email address. Please check your inbox and enter the 6-digit code below.",
        'success',
        'OTP Sent Successfully'
      );
    } catch (error) {
      showNotification(
        "The email address you entered is not associated with any account in our system. Please verify your email address and try again.",
        'error',
        'Email Not Found'
      );
    } finally {
      setSendOtpLoading(false);
      if (emailInputRef.current) {
        emailInputRef.current.blur(); 
      }
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setVerifyOtpLoading(true);
    try {
      const response = await axios.post(
        "https://car.amar.co.jp/api/authorization/auth/forgot-password/verify-otp",
        {
          contactInfo: email,
          otp: otp,
        }
      );
  
      console.log("OTP Verification Response:", response.data);
  
      if (response.data?.success || response.data?.status === "success") {
        setIsOtpVerified(true);
        showNotification(
          "Your identity has been successfully verified. You can now proceed to create a new password for your account.",
          'success',
          'Verification Successful'
        );
      } else {
        throw new Error(response.data?.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      showNotification(
        "The verification code you entered is incorrect or has expired. Please check the code and try again, or request a new OTP.",
        'error',
        'Verification Failed'
      );
      document.activeElement.blur();
    } finally {
      setVerifyOtpLoading(false);
    }
  };
  
  const resendOtp = async (e) => {
    e.preventDefault();
    setResendOtpLoading(true);
    try {
      await axios.post(`https://car.amar.co.jp/api/authorization/auth/forgot-password/send-otp?passwordResetRequestEmailId=${email}`);
      showNotification(
        "A new verification code has been sent to your email address. Please check your inbox for the updated code.",
        'success',
        'New OTP Sent'
      );
    } catch (error) {
      showNotification(
        "We encountered an issue while sending the verification code. Please check your internet connection and try again.",
        'error',
        'Resend Failed'
      );
    } finally {
      setResendOtpLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showNotification(
        "The passwords you entered do not match. Please ensure both password fields contain the same information.",
        'warning',
        'Password Mismatch'
      );
      return;
    }
    setResetPasswordLoading(true);
    try {
      await axios.post(
        "https://car.amar.co.jp/api/authorization/auth/forgot-password",
        {
          userEmailId: email,
          newPassword: newPassword,
          confirmPassword: confirmPassword,
        }
      );
      showNotification(
        "Your password has been successfully updated. You can now log in with your new credentials.",
        'success',
        'Password Reset Complete',
        8000
      );
      setTimeout(() => {
        setShowPopup(false);
        navigate("/login");
      }, 2000);
    } catch (error) {
      showNotification(
        "We encountered an issue while updating your password. Please try again or contact support if the problem persists.",
        'error',
        'Password Reset Failed'
      );
      document.activeElement.blur();
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await axios.post(
        "https://car.amar.co.jp/api/authorization/auth/login",
        formData,
        {
          headers: {
            "Accept-Language": "en",
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);
        if (response.data.userId) localStorage.setItem("userId", String(response.data.userId));
        showNotification(
          "Welcome back! You have been successfully logged in and will be redirected to your dashboard.",
          'success',
          'Login Successful',
          4000
        );
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showNotification(
          "Authentication failed. Please check your credentials and try again.",
          'error',
          'Login Failed'
        );
      }
    } catch (error) {
      showNotification(
        error.response?.data?.message || "Unable to complete login. Please verify your credentials and try again.",
        'error',
        'Authentication Error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setIsOtpSent(false);
    setIsOtpVerified(false);
  };

  return (
    <Box
      // Use the main Box for the full-screen background
      sx={{
        p: 0,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: 'url(/image/shutterstock.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        width: '100%',
      }}
    >
      
      {/* Overlay for background darkening and text clarity */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark overlay
          zIndex: 1, // Place behind content
        }}
      />

      {/* Enhanced Notification System */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ 
          mt: 2,
          zIndex: 10, // Ensure notifications are always on top
          '& .MuiSnackbarContent-root': {
            minWidth: '400px',
            maxWidth: '600px'
          }
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '14px',
            '& .MuiAlert-message': {
              padding: '8px 0'
            }
          }}
        >
          {notification.title && (
            <AlertTitle sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {notification.title}
            </AlertTitle>
          )}
          {notification.message}
        </Alert>
      </Snackbar>
     
      {/* Content wrapper to float above the background overlay */}
      <Box 
        sx={{ 
          width: '100%', 
          zIndex: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ width: "100%" }}><Notes /></Box>

        {/* Simplified Header Text Section (Breadcrumb) - New Design */}
        <Box
          sx={{
            width: '100%',
            color: 'white',
            pt: 4,
            pb: 4, 
            px: { xs: 2, md: 8 }, 
            mb: 4, 
            textAlign: 'left',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#1E1E1E', // Darker gray/black for the pill
              padding: '10px 15px',
              borderRadius: '8px', // Rounded corners
              display: 'inline-block', // Shrink to content width
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '15px',
                color: '#A9A9A9', // Gray color for 'Home /'
                fontWeight: '400',
                marginRight: '5px',
              }}
            >
              Home / 
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '15px',
                color: 'white', // White color for 'Login'
                fontWeight: '600',
              }}
            >
              Login
            </Typography>
          </Box>
        </Box>


        {/* Centralized Login Form Container - Simplified layout, removing the side image */}
        <Box
          sx={{
            width: { xs: '85%', sm: '70%', md: '40%', lg: '30%' },
            backgroundColor: "#FFFFFF",
            border: "1px solid #EBEBEB",
            borderRadius: "10px",
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: 3,
            mb: 8, // Margin bottom for overall layout
          }}
        >
          {/* Header/Title Box */}
          <Box
            sx={{
              backgroundColor: "#fafafa",
              height: "50px",
              borderBottom: "1px solid #ebebeb",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mb: 2,
              px: 2,
            }}
          >
            <Box
              component="img"
              src="/image/Login-icon-image.svg"
              sx={{
                height: "25px",
              }}
            />
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "18px",
                color: '#333'
              }}
            >
              LOGIN
            </Typography>
          </Box>

          {/* Form Fields Section */}
          <Box sx={{ padding: "0 20px" }}>
            <form onSubmit={handleSubmit}>
              <label
                htmlFor="contactInfo"
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "600",
                  marginTop: "20px",
                }}
              >
                <span style={{ color: "red" }}>*</span> Required fields
              </label>

              <input
                id="contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  margin: "10px 0",
                  // border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
                placeholder="* Enter Email or Mobile Number"
                required
              />

              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    paddingRight: "40px",
                    margin: "10px 0",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                  placeholder="* Enter Password"
                  required
                />

                <span
                  onMouseEnter={() => setShowPassword(true)}
                  onMouseLeave={() => setShowPassword(false)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#777",
                  }}
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </span>
              </div>

              <button 
                type="button" 
                className="forgot-password-btn" 
                onClick={handleForgotPasswordClick}
                style={{
                  background: "none",
                  border: "none",
                  color: "#0000EE",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: "14px",
                  fontFamily: "inherit",
                  textAlign: "right",
                  display: "block",
                  marginLeft: "auto",
                  marginBottom: "10px"
                }}
              >
                Forgot your password?
              </button>

              <Stack direction="row">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#eb2329",
                    width: "100%",
                    marginTop: "20px",
                    padding: "12px 0",
                    fontSize: "16px",
                    fontWeight: 'bold',
                    ":hover": {
                      backgroundColor: "#d02026",
                    },
                  }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "LOGGING IN..." : "LOGIN NOW"}
                </Button>
              </Stack>
            </form>

            <Typography sx={{ fontSize: "15px", textAlign: 'center', marginTop: "15px", marginBottom: '10px' }}>
              <a
                href="/signUp" // Use relative path for React router or full URL if needed
                onClick={(e) => {
                  e.preventDefault(); 
                  window.location.href = "https://car.amar.co.jp/signUp"; // Retain original navigation
                }}
                style={{
                  cursor: "pointer", 
                  fontSize: "15px", 
                  fontWeight: "bold", 
                  color: "#eb2329", 
                  marginLeft: "5px",
                  textDecoration: 'none'
                }}
              >
                Create Account / SignUp
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Forgot Password Popup (retained its original styling and logic) */}
      {showPopup && (
        <div className="overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
          <div className="popup-content" style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              width: '350px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
          }}>
            <span className="close-btn" onClick={handleClosePopup} style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#333'
            }}>✖</span>

            {!isOtpSent && (
              <>
                <span style={{ 
                  fontSize: "16px", 
                  fontWeight: "500", 
                  color: "black", 
                  display: "block", 
                  marginBottom: "8px", 
                  textTransform: "capitalize", 
                  textAlign:"left" 
                }}>
                  *Enter your registered email
                </span>

                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  ref={emailInputRef} 
                  style={{
                    width: "100%",
                    height: "45px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "border-color 0.3s ease",
                    boxSizing: 'border-box'
                  }}
                /> 
        
                <button 
                  onClick={sendOtp} 
                  disabled={sendOtpLoading}
                  style={{
                    backgroundColor: sendOtpLoading ? "#ccc" : "#eb2329",
                    color: "white",
                    padding: "10px 16px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    border: "none",
                    borderRadius: "5px",
                    cursor: sendOtpLoading ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    transition: "background-color 0.3s ease",
                    marginTop: "15px",
                    alignSelf: 'center',
                    position: "relative",
                    minWidth: "120px"
                  }}
                >
                  {sendOtpLoading ? (
                    <>
                      Sending...
                      <span style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        display: "inline-block"
                      }}></span>
                    </>
                  ) : "Send OTP"}
                </button>
              </>
            )}

            {isOtpSent && !isOtpVerified && (
              <>
                <input
                  type="text"
                  placeholder="Enter 6 Digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  style={{
                    width: "100%",
                    height: "45px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "border-color 0.3s ease",
                    boxSizing: 'border-box'
                  }}
                />

                <div style={{ display: "flex", gap: "20px", marginTop: '15px', alignSelf: 'center' }}>
                  <button 
                    onClick={verifyOtp} 
                    disabled={verifyOtpLoading}
                    style={{
                      backgroundColor: verifyOtpLoading ? "#ccc" : "#eb2329",
                      color: "white",
                      padding: "10px 16px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "5px",
                      cursor: verifyOtpLoading ? "not-allowed" : "pointer",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      transition: "background-color 0.3s ease",
                      position: "relative",
                      minWidth: "120px"
                    }}
                  >
                    {verifyOtpLoading ? (
                      <>
                        Verifying...
                        <span style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          display: "inline-block"
                        }}></span>
                      </>
                    ) : "Verify OTP"}
                  </button>

                  <button
                    onClick={resendOtp}
                    disabled={resendOtpLoading}
                    style={{
                      backgroundColor: resendOtpLoading ? "#ccc" : "#eb2329",
                      color: "white",
                      padding: "10px 16px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "5px",
                      cursor: resendOtpLoading ? "not-allowed" : "pointer",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      transition: "background-color 0.3s ease",
                      position: "relative",
                      minWidth: "140px"
                    }}
                  >
                    {resendOtpLoading ? (
                      <>
                        Sending...
                        <span style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          display: "inline-block"
                        }}></span>
                      </>
                    ) : "Send OTP Again"}
                  </button>
                </div>
              </>
            )}

            {isOtpVerified && (
              <>
                <input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    height: "45px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "border-color 0.3s ease",
                    boxSizing: 'border-box'
                  }}
                />

                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: "100%",
                    height: "45px",
                    marginTop: "10px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "border-color 0.3s ease",
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  onClick={resetPassword} 
                  disabled={resetPasswordLoading}
                  style={{
                    backgroundColor: resetPasswordLoading ? "#ccc" : "#eb2329",
                    color: "white",
                    padding: "10px 16px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    border: "none",
                    borderRadius: "5px",
                    cursor: resetPasswordLoading ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    transition: "background-color 0.3s ease",
                    marginTop: "15px",
                    alignSelf: 'center',
                    position: "relative",
                    minWidth: "140px"
                  }}
                >
                  {resetPasswordLoading ? (
                    <>
                      Resetting...
                      <span style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "white",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        display: "inline-block"
                      }}></span>
                    </>
                  ) : "Reset Password"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: translateY(-50%) rotate(0deg); }
          100% { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>

      {/* Footer component - ensured 100% width and correct z-index */}
      <Box sx={{ width: '100%', mt: 'auto', zIndex: 2 }}>
        <FooterManual />
      </Box>
    </Box>
  );
};

export default Login;